import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { AlertTriangle, BookOpenText, CheckCircle2, Gauge, GraduationCap, ListChecks, ShieldCheck, Sliders } from 'lucide-react';
import { C, R } from '../ui/kit';
import { KATALOG, BLOECKE, GRUPPEN, V2_KATALOG } from './EngineGuideKatalog';
import './Engine.css';

// ENGINE-GUIDE-KATALOG-001 (Owner-Feedback 07.08.2026): „Zu oberflächlich, Regler fehlen,
// Schrift passt nicht zum UI. Eine Anleitung wie bei einer Maschine: Wie bediene ich sie,
// was macht JEDE Zahl, was passiert bei anderen Werten."
//
// Antwort: (1) Der Regler-Katalog deckt JEDEN einstellbaren V1-Wert und alle V2-Parameter ab
// — ein Backend-Vertragstest erzwingt die Vollständigkeit gegen die echte Engine-Config.
// (2) Typografie und Bausteine sind die des Engine-Reiters (card/panel/12–13 px), kein
// eigener Look. (3) Read-only bleibt: nichts hier schreibt Konfiguration; Live-Zahlen kommen
// nur aus den drei kanonischen GET-Quellen — bei Ausfall steht „nicht verfügbar", nie ein Default.

// ── Design-Tokens: identisch zum Engine-Reiter (Engine.js) ────────────────────
const card = { background: C.card, border: `1px solid ${C.line}`, borderRadius: R.lg, padding: 16, marginBottom: 16 };
const panel = { background: '#fafafb', border: '1px solid #edeef1', borderRadius: 10, padding: 14, marginBottom: 12 };
const panelTitle = { fontWeight: 800, fontSize: 13, color: '#1f2937', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #edeef1' };
const hintS = { fontSize: 12, color: '#4b5563', display: 'block', marginTop: 6, lineHeight: 1.5 };
const txt = { fontSize: 13, color: '#374151', lineHeight: 1.55 };
const h3 = { margin: '0 0 8px', fontSize: 13, fontWeight: 800, color: '#1f2937', display: 'flex', gap: 6, alignItems: 'center' };
const wertChip = { background: '#eef2ff', color: '#3730a3', borderRadius: 7, padding: '1px 8px', fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap' };
const pfadChip = { background: '#f3f4f6', color: '#4b5563', borderRadius: 5, padding: '0 5px', fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace' };
const sourceInitial = { status: 'loading', data: null };

const CHAPTERS = [
  { id: 'ueberblick', label: 'Überblick' },
  { id: 'bedienung', label: 'Bedienung' },
  { id: 'katalog', label: 'Regler-Katalog (V1)' },
  { id: 'lernen', label: 'Lernen & Wochenprofil' },
  { id: 'v2', label: 'Ultimate Markt V2' },
  { id: 'faq', label: 'Sicherheit & FAQ' },
];

function isPresent(value) { return value !== null && value !== undefined && value !== ''; }
function fmtNumber(value, maxDigits = 3) {
  if (!isPresent(value) || !Number.isFinite(Number(value))) return null;
  return Number(value).toLocaleString('de-DE', { maximumFractionDigits: maxDigits });
}
// Kurzname eines Taxameter-Abschnitts aus den Grenzen — die letzte Grenze ist offen und wird nie genannt.
function abschnittKurz(stufen, i) {
  const unten = i === 0 ? 0 : stufen[i - 1]?.max;
  if (i === stufen.length - 1) return i === 0 ? 'alle km' : `über ${fmtNumber(unten)} km`;
  return i === 0 ? `bis ${fmtNumber(stufen[i]?.max)} km` : `${fmtNumber(unten)}–${fmtNumber(stufen[i]?.max)} km`;
}
function shortHash(value) {
  if (!isPresent(value)) return '—';
  const text = String(value);
  return text.length > 12 ? `${text.slice(0, 12)}…` : text;
}
function getPath(obj, pfad) {
  let node = obj;
  for (const teil of String(pfad).split('.')) {
    if (node === null || node === undefined) return undefined;
    node = node[teil];
  }
  return node;
}
/** Live-Wert-Anzeige: echte Zahl/Text aus der Config — nie ein erfundener Default. */
function liveWert(config, pfad) {
  if (!config) return 'nicht verfügbar';
  const wert = getPath(config, pfad);
  if (wert === undefined || wert === null) return 'nicht gesetzt';
  if (typeof wert === 'boolean') return wert ? 'an' : 'aus';
  if (typeof wert === 'string') return wert;
  const zahl = fmtNumber(wert);
  return zahl === null ? String(wert) : zahl;
}

function StatusBadge({ ok, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, padding: '3px 8px', background: ok ? '#ecfdf5' : '#fff7ed', color: ok ? '#166534' : '#9a3412', fontSize: 13, fontWeight: 800 }}>
      {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}{children}
    </span>
  );
}

function SourceCard({ sourceId, title, source, children }) {
  return (
    <section data-source={sourceId} className="engine-guide-source" style={panel} aria-labelledby={`${sourceId}-title`}>
      <h3 id={`${sourceId}-title`} style={panelTitle}>{title}</h3>
      {source.status === 'loading' && <p style={hintS}>Aktueller Zustand wird geladen …</p>}
      {source.status === 'error' && (
        <div role="status" style={{ display: 'flex', gap: 7, alignItems: 'flex-start', color: '#9a3412', fontSize: 13, lineHeight: 1.5 }}>
          <AlertTriangle size={15} style={{ flex: '0 0 auto', marginTop: 2 }} />
          <span><b>Aktueller Zustand nicht verfügbar</b><br />Es wird kein Werkseinstellungswert als Live-Wert ausgegeben.</span>
        </div>
      )}
      {source.status === 'ready' && children}
    </section>
  );
}

function KeyValues({ rows }) {
  return (
    <dl className="engine-guide-values" style={{ margin: 0 }}>
      {rows.map(([label, value]) => (
        <div className="engine-guide-value" key={label} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.9fr) minmax(0, 1.1fr)', gap: 10, padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
          <dt style={{ color: '#4b5563' }}>{label}</dt>
          <dd style={{ margin: 0, fontWeight: 750, textAlign: 'right', overflowWrap: 'anywhere' }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Katalog-Bausteine ─────────────────────────────────────────────────────────

function Eintrag({ pfad, config }) {
  const e = KATALOG[pfad];
  if (!e) return null;
  return (
    <div data-katalog={pfad} style={{ borderBottom: '1px solid #edeef1', padding: '10px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 3 }}>
        <span style={{ fontWeight: 800, fontSize: 13, color: '#1f2937' }}>{e.name} <code style={pfadChip}>{pfad}</code></span>
        <span style={wertChip}>Aktuell: {liveWert(config, pfad)}{e.einheit && !['an/aus', 'aus/schatten/an'].includes(e.einheit) ? ` ${e.einheit}` : ''}</span>
      </div>
      <div style={txt}>{e.was}</div>
      {e.beispiel && <div style={{ ...txt, marginTop: 3 }}><b>Beispiel:</b> {e.beispiel}</div>}
      {(e.hoch || e.runter) && (
        <div style={{ ...txt, marginTop: 3 }}>
          {e.hoch && <span><b style={{ color: '#166534' }}>Höher →</b> {e.hoch} </span>}
          {e.runter && <span><b style={{ color: '#9a3412' }}>Niedriger →</b> {e.runter}</span>}
        </div>
      )}
      {e.warnung && <div style={{ ...txt, marginTop: 3, color: '#9a3412' }}>⚠️ {e.warnung}</div>}
    </div>
  );
}

function Block({ id }) {
  const b = BLOECKE[id];
  if (!b) return null;
  return (
    <div data-katalog={id} style={{ background: '#fff', border: '1px solid #edeef1', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: '#1f2937', marginBottom: 3 }}>{b.name} <code style={pfadChip}>{id}</code> <span style={{ ...pfadChip, background: '#eef2ff', color: '#3730a3' }}>Listen-Block — als Ganzes gepflegt</span></div>
      <div style={txt}>{b.was}</div>
      {b.beispiel && <div style={{ ...txt, marginTop: 3 }}><b>Beispiel:</b> {b.beispiel}</div>}
      {b.warnung && <div style={{ ...txt, marginTop: 3, color: '#9a3412' }}>⚠️ {b.warnung}</div>}
    </div>
  );
}

function V2Eintrag({ schluessel, params }) {
  const e = V2_KATALOG[schluessel];
  if (!e) return null;
  const wert = params ? params[schluessel] : undefined;
  const anzeige = schluessel === 'curveAnchors'
    ? (Array.isArray(wert) ? wert.map(([x, y]) => `${fmtNumber(x, 2)}→${fmtNumber(y, 2)}`).join(' · ') : 'nicht verfügbar')
    : (params ? (fmtNumber(wert) ?? String(wert)) : 'nicht verfügbar');
  return (
    <div data-katalog={`v2.${schluessel}`} style={{ borderBottom: '1px solid #edeef1', padding: '10px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 3 }}>
        <span style={{ fontWeight: 800, fontSize: 13, color: '#1f2937' }}>{e.name} <code style={pfadChip}>{schluessel}</code></span>
        <span style={wertChip}>Aktuell: {anzeige}{e.einheit && !['ρ → ×'].includes(e.einheit) ? ` ${e.einheit}` : ''}</span>
      </div>
      <div style={txt}>{e.was}</div>
      {e.beispiel && <div style={{ ...txt, marginTop: 3 }}><b>Beispiel:</b> {e.beispiel}</div>}
      {(e.hoch || e.runter) && (
        <div style={{ ...txt, marginTop: 3 }}>
          {e.hoch && <span><b style={{ color: '#166534' }}>Höher →</b> {e.hoch} </span>}
          {e.runter && <span><b style={{ color: '#9a3412' }}>Niedriger →</b> {e.runter}</span>}
        </div>
      )}
      {e.warnung && <div style={{ ...txt, marginTop: 3, color: '#9a3412' }}>⚠️ {e.warnung}</div>}
    </div>
  );
}

// ── Kapitel ───────────────────────────────────────────────────────────────────

function UeberblickChapter({ sources }) {
  const config = sources.v1.data?.config;
  const state = sources.v2State.data;
  return (
    <>
      <section style={{ ...card, borderColor: '#e9d5ff', background: '#faf5ff' }}>
        <h3 style={h3}><ShieldCheck size={15} /> Die drei Grundsätze dieser Maschine</h3>
        <ol style={{ ...txt, margin: 0, paddingLeft: 18 }}>
          <li><b>Der Markt sagt dir nie, was du verlangst.</b> Die Markt-Referenz ist ausschließlich Anzeige und Analyse — nie Preisquelle, Deckel oder Verfügbarkeits-Gate.</li>
          <li><b>Die Engine lernt Fakten, du machst Politik.</b> Kein Instrument verstellt je selbst einen Regler; alles, was Preise ändert, braucht deinen Klick mit Pflichtgrund — protokolliert und rückrollbar.</li>
          <li><b>Datenmangel macht nie teurer.</b> Wo Daten fehlen, bleibt alles neutral oder wird günstiger — nie umgekehrt.</li>
        </ol>
      </section>

      <section style={card}>
        <h3 style={h3}><Sliders size={15} /> So entsteht jeder Preis (V1) — die Kette</h3>
        <pre style={{ background: '#0a0a0a', color: '#e5e7eb', borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 1.8, overflowX: 'auto', margin: '0 0 8px' }}>
{`Taxameter-Grundpreis (Sätze je Streckenabschnitt, eine Grundgebühr)
  × Fahrzeugklasse   × Stadt-Faktor
  × Marktlage (Push … Surge — Knappheit an Autos; mit V2 der V2-Marktfaktor)
  × Wochenprofil T (Spitzenstunde teurer, Flaute günstiger)
  × B1-Ereignis-Aufschlag (nur wenn jetzt unerwartet viel los ist)
  × ggf. Bar-Rabatt
  → ggf. Routen-Rabatt  × ggf. Zeitregel
  ⌃ gemeinsamer Deckel (Marktlage × T × B1 × Zeitregel-Aufschlag — kürzt B1, dann T, dann Zeitregel; nie die Marktlage)
  → eigener Stack-Floor  → eigener Mindestpreis

Markt-Referenz → getrennte Anzeige; kein Pfeil zurück in die Preiskette`}
        </pre>
        <span style={hintS}>Reservierungen laufen immer neutral (V1-Vertrag, Marktlage ausgeglichen, card-only) — der Zufalls-Moment der Buchung darf den Preis einer Fahrt zu ganz anderer Uhrzeit nicht bestimmen. Der Preis ist serverautoritativ und quotegebunden; ein vom Client übermittelter Preis wird nicht geehrt, unbekannte Abholgebiete sind fail-closed. Der 2-%-… bzw. aktuell {config?.take ? fmtNumber(config.take.cashDiscount * 100, 1) : '—'}-%-Barrabatt wird im serverseitigen Quote gebunden und darf im Estimate angezeigt werden.</span>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <SourceCard sourceId="v1" title="Deine Live-Werte · Ultimate V1 (rechnet heute jeden Preis)" source={sources.v1}>
          {config ? (
            <KeyValues rows={[
              ['Grundgebühr / km-Sätze', Array.isArray(config.tariffByTier) ? `${fmtNumber(config.tariffByTier[0]?.base)} € · ${config.tariffByTier.map((t, i, stufen) => `${fmtNumber(t?.perKm)} (${abschnittKurz(stufen, i)})`).join(' / ')} €/km` : '—'],
              ['Marktlage', config.dynamic ? `Boden ${fmtNumber(config.dynamic.pushFloor)}× · Deckel ${fmtNumber(config.dynamic.surgeCap)}× · Start-ρ ${fmtNumber(config.dynamic.baselineRho)}` : '—'],
              ['Gemeinsamer Deckel', config.dynamic?.combinedCap != null ? `${fmtNumber(config.dynamic.combinedCap)}×` : 'nicht gesetzt'],
              ['Wochenprofil (T)', config.weekProfile ? `${config.weekProfile.mode} · g ${fmtNumber(config.weekProfile.g)} · ${fmtNumber(config.weekProfile.min)}–${fmtNumber(config.weekProfile.max)}×` : '—'],
              ['B1-Ereignis-Aufschlag', config.demandPremium ? `${config.demandPremium.mode ?? 'off'} · bis ×${fmtNumber(config.demandPremium.peak) ?? '—'}` : '—'],
              ['Provision', config.take ? `${fmtNumber(config.take.min * 100, 1)}–${fmtNumber(config.take.max * 100, 1)} % + ${fmtNumber(config.take.fee)} €` : '—'],
              ['Mindestpreis', config.minPrice != null ? `${fmtNumber(config.minPrice)} €` : '—'],
            ]} />
          ) : <p style={hintS}>Die Antwort enthält keine V1-Konfiguration; es wird kein Ersatzwert angenommen.</p>}
        </SourceCard>
        <SourceCard sourceId="v2-state" title="Deine Live-Werte · Ultimate Markt V2 (Nachfolger)" source={sources.v2State}>
          {state ? (
            <KeyValues rows={[
              ['Marktmodus', state.market_mode ?? '—'],
              ['Behavior / Telemetrie / Learning', `${state.behavior_mode ?? '—'} / ${state.telemetry_mode ?? '—'} / ${state.learning_mode ?? '—'}`],
              ['Aktive Revision', state.active_revision ? `#${state.active_revision.rev} · ${shortHash(state.active_revision.hash)}` : 'keine gespeicherte Revision'],
              ['Snapshot', state.snapshot_health ? (state.snapshot_health.ok ? `frisch + passend · ${state.snapshot_health.zones ?? '—'} Zonen` : (state.snapshot_health.missing ? 'fehlend' : `blockiert · ${state.snapshot_health.reason || 'stale'}`)) : '—'],
              ['Producer', state.producer_active === true ? 'running' : state.producer_active === false ? 'dormant' : '—'],
              ['Kill-Switch', state.kill && Object.entries(state.kill).filter(([n, on]) => n !== 'heatmap' && on).length ? 'AKTIV' : 'kein Kill-Switch aktiv'],
            ]} />
          ) : <p style={hintS}>Die Antwort enthält keinen V2-Systemzustand.</p>}
        </SourceCard>
      </div>
    </>
  );
}

function BedienungChapter() {
  const schritt = (nr, titel, inhalt) => (
    <div style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 22, height: 22, borderRadius: 999, background: '#0a0a0a', color: '#fff', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{nr}</div>
      <div><div style={{ fontWeight: 800, fontSize: 13, color: '#1f2937' }}>{titel}</div><div style={txt}>{inhalt}</div></div>
    </div>
  );
  return (
    <>
      <section style={card}>
        <h3 style={h3}><ListChecks size={15} /> Einen V1-Regler ändern — der sichere Weg (immer gleich)</h3>
        {schritt(1, 'Nachschlagen', 'Im Regler-Katalog (nächster Reiter) lesen, was die Zahl macht und was Höher/Niedriger bewirkt.')}
        {schritt(2, 'Gefahrlos durchrechnen', 'In der Karte „Preisvorschau — jede Lage durchrechnen" (Engine → Ultimate, unten) Lage, Distanz, Stadt, Klasse und Zahlart einstellen. Der persistenzfreie Rechner zeigt Preis, Abzug, Partner-Anteil und Wochenprofil-Faktor, ohne eine Fahrt anzulegen.')}
        {schritt(3, 'Wert eintragen', 'In der Karte „Ultimate-Parameter" das Feld ändern. Ein geänderter Wert ist noch NICHT aktiv.')}
        {schritt(4, 'Speichern', 'Erst der „Speichern"-Knopf schreibt — die Engine übernimmt binnen ~30 Sekunden.')}
        {schritt(5, 'Prüfen', 'In „Live jetzt" und der Preisvorschau gegenprüfen. Die Marktbild-Karte zeigt getrennt nur die rückblickende Position; sie ist kein Preisziel und kein Freigabe-Gate.')}
        {schritt(6, 'Notfalls zurück', '„Werkseinstellung" setzt alle Regler auf Werk (deine Werte werden als „Rückgängig" gesichert) · „Rückgängig" stellt sie wieder her.')}
      </section>

      <section style={{ ...card, borderColor: '#fed7aa', background: '#fff7ed' }}>
        <h3 style={h3}>Markt-Anzeige und Go-Live-Lernschnitt</h3>
        <div style={txt}>
          <b>Marktpreise und Marktreferenzen sind ausschließlich Anzeige.</b> Nur die daraus getrennt berechnete relative Nachfrageform samt Belegmenge kann der Owner vor Go-Live über „Aktuellen Uploadstand als Startprofil setzen" in Wochenprofil T übernehmen. Bis Go-Live ist dieser vollständige Snapshot nach jedem Wochen-Upload ersetzbar.<br />
          <b>🔒 „Go-Live starten"</b> — setzt nach doppelter Bestätigung den irreversiblen Lern-Cutover und sperrt genau das zuvor gesetzte aktuelle Startprofil. Spätere Uploads verändern T nicht. Erst zukünftige eigene Ultimate-Fahrten ersetzen es je PLZ × Wochentag × Stunde. Der Klick ändert keinen Tarif, Fahrpreis oder Geldwert und löscht keine Daten.
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}><Gauge size={15} /> V2 bedienen (Admin → „Ultimate Pricing V2")</h3>
        <div style={txt}>
          <b>Jede Änderung verlangt einen Pflichtgrund</b> und wird auditiert; parallele Fremdänderung → Konflikt statt stillem Überschreiben.<br />
          <b>Marktmodus:</b> nur Stufe für Stufe vorwärts (off → shadow → test → live), zurück jederzeit beliebig weit. „live" braucht den Prüfbuch-Schlüssel und frische, config-passende Messungen.<br />
          <b>Config ändern:</b> Draft → Simulation (D=3/5/10-Szenarien) → anlegen → aktivieren (nie im scharfen Modus) → notfalls „Rollback auf vorherige Revision". Vertragskonstanten (Boden 0,75 · Radius 4,5) sind gar nicht erst editierbar.<br />
          <b>Not-Aus:</b> darf auch ein Manager ziehen — aufheben nur der Owner; der Markt startet danach wieder bei off.<br />
          <b>Nachschlagen:</b> Kurvensimulator (Szenarien) und Quote-Suche (komplette Herleitung jeder einzelnen Quote: Q, D, ρ, Faktoren, V1- vs. V2-Preis, Gründe) sind read-only.
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>Empfehlungen der Analyse anwenden (KI-Agent → „Ultimate-Empfehlungen")</h3>
        <div style={txt}>Die Analyse erzeugt Vorschläge mit Beleg, Konfidenz und Rollback-Wert — <b>niemals automatisch angewendet</b>. Dein Weg je Zeile: Beleg lesen (Evidenz n, Backtest, Risiko) → „Anwenden" → Bestätigungsdialog mit den exakten Werten → geschrieben, protokolliert, jederzeit ein Klick zurück. Vertraglich Gebundenes (Provision, Push-Boden, Radius) bietet die Analyse gar nicht erst an.</div>
      </section>
    </>
  );
}

function KatalogChapter({ sources }) {
  const config = sources.v1.data?.config;
  return (
    <>
      <section style={{ ...card, marginBottom: 12 }}>
        <div style={txt}><b>Jeder einstellbare Wert der V1-Engine</b> — was er macht, ein konkretes Beispiel, was Höher/Niedriger bewirkt, und dein aktueller Live-Wert (blauer Chip). Gruppiert wie die Panels im Engine-Reiter. {sources.v1.status === 'error' ? <b style={{ color: '#9a3412' }}>Live-Werte derzeit nicht verfügbar — die Erklärungen gelten trotzdem.</b> : null}</div>
      </section>
      {GRUPPEN.map((gruppe) => (
        <section key={gruppe.titel} style={panel}>
          <div style={panelTitle}>{gruppe.titel}</div>
          {(gruppe.bloecke || []).map((id) => <Block key={id} id={id} />)}
          {gruppe.pfade.map((pfad) => <Eintrag key={pfad} pfad={pfad} config={config} />)}
        </section>
      ))}
    </>
  );
}

function LernenChapter() {
  return (
    <>
      <section style={card}>
        <h3 style={h3}><GraduationCap size={15} /> Die drei Uhren — jede lernt etwas anderes</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {[
            ['Sekunden', 'Marktlage ρ', 'Sind JETZT genug Autos da? 2-s-Takt — rauf träge, runter schnell.'],
            ['Minuten', 'B1-Ereignis-Aufschlag', 'Ist JETZT mehr los als nach dem gespeicherten Normal DIESER Wochenstunde erwartet? Letzte Minuten (Fenster), nur Aufschlag.'],
            ['Wochen', 'Normale → Wochenprofil', 'Wann ist bei uns überhaupt viel los? Eine Beobachtung je PLZ × Stunde.'],
          ].map(([uhr, wer, frage]) => (
            <div key={uhr} style={{ ...panel, marginBottom: 0, textAlign: 'center' }}>
              <div style={{ fontWeight: 850, fontSize: 13 }}>{uhr}</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a', margin: '2px 0 4px' }}>{wer}</div>
              <div style={{ ...txt, fontSize: 12 }}>{frage}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={card}>
        <h3 style={h3}>So lernt die Normale (startet erst mit deinem 🔒-Klick)</h3>
        <ul style={{ ...txt, margin: 0, paddingLeft: 18 }}>
          <li>Vor Go-Live setzt der Owner den aktuellen Uploadstand als relative Startform. Marktpreise und Marktreferenzen bleiben dabei vollständig draußen.</li>
          <li>Nach Go-Live zählt der Server echte, lernberechtigte eigene Fahrtwünsche in der passenden PLZ-/Wochenzelle. Reservierungen zählen bei ihrer geplanten Fahrzeit. Angerissene Stunden (unter 15 Minuten Messung) werden verworfen.</li>
          <li>Je Zelle gilt exakt: vollständige Ersetzung bei eigenen Fahrten in Höhe von <b>10 % der übernommenen Upload-Belege</b>. Dazwischen mischt der Server linear; es gibt kein verborgenes Pauschalgewicht.</li>
          <li>Spätere Uploads bleiben nach Go-Live nur Marktansicht und können den gesperrten Snapshot technisch nicht verändern.</li>
        </ul>
      </section>

      <section style={{ ...card, borderColor: '#bbf7d0', background: '#f0fdf4' }}>
        <h3 style={h3}>Warum sich nichts aufschaukelt</h3>
        <ul style={{ ...txt, margin: 0, paddingLeft: 18 }}>
          <li>T lernt aus Nachfrage zu den eigenen Preisen — jede Rückkopplung ist <b>gedämpft</b> (Durchgriff nur anteilig, träge Saat, hartes Band, gemeinsamer Deckel).</li>
          <li>Die Wochenwelle wird nur <b>einmal</b> bepreist: T = Plan · B1 = Abweichung vom gespeicherten Normal (die Erwartung enthält die Form derselben Stunde) · Surge = Knappheit.</li>
          <li><b>Kein Auto-Apply, nirgends:</b> Gelernt werden Fakten — jeder Reglerwert bleibt deine Entscheidung.</li>
        </ul>
      </section>
    </>
  );
}

function V2Chapter({ sources }) {
  const params = sources.v2Config.data?.active?.params;
  const active = sources.v2Config.data?.active;
  return (
    <>
      <section style={{ ...card, borderColor: '#bfdbfe', background: '#eff6ff' }}>
        <h3 style={h3}><Gauge size={15} /> Was V2 anders macht</h3>
        <div style={txt}>
          Statt Zonen-Schätzung misst V2 <b>exakt am Abholpunkt</b>: Q = offene Sofortfahrt-Nachfrage und D = tatsächlich geeignete, erreichbare Fahrer im 4,5-km-Radius (echte Matching-Regeln — nie bloß registrierte Fahrer); <b>ρ = Q ÷ D</b>. Vertragliches Mindestgate: <b>Q &lt; 5 oder D &lt; 3 → Faktor immer 0,75</b> — Fahrermangel allein erhöht nie den Preis. Surge erst ab ρ = 5/3 nach 30 s ununterbrochener Bestätigung, Zielkurve bis Deckel 1,75, eine EMA-Glättung, Anzeige in 0,05er-Schritten. Behavior ist deterministisch, <b>discount_only</b> = nur Rabatte für erkennbare Zögerer (full serverseitig gesperrt).<br /><br />
          <b>Rückfall-Matrix (exakt):</b> Zurück zu <b>V1</b> bei ungültiger aktiver Config, fehlendem <code>LEDGER_HMAC_KEY</code> in den global wirksamen Modi test/live, Kill oder off. Auf den <b>Boden 0,75 (fail-cheap, NICHT V1)</b> dagegen, wenn V2 bereits scharf läuft und der Snapshot fehlt, stale oder revisionsfremd ist. Learning ist Proposal-only — <b>kein Auto-Apply</b>; Apply ist ein manueller Owner-Akt außerhalb eines global wirksamen Marktmodus (Evidenz sammeln → zurückstufen → anwenden → frische Snapshots → test → live). Die read-only Admin-Zonenansicht bleibt im V2-Cockpit.
        </div>
      </section>

      <section style={panel}>
        <div style={panelTitle}>V2-Regler-Katalog — alle Vertragsparameter {active ? `(aktive Revision #${active.rev ?? '—'})` : '(Revision nicht verfügbar)'}</div>
        {Object.keys(V2_KATALOG).map((schluessel) => <V2Eintrag key={schluessel} schluessel={schluessel} params={params} />)}
      </section>

      <SourceCard sourceId="v2-config" title="Aktive V2-Revision (Live-Quelle)" source={sources.v2Config}>
        {params ? (
          <KeyValues rows={[
            ['Revision / Hash', `#${active.rev ?? '—'} · ${shortHash(active.hash)}`],
            ['Mindestgates', `Q < ${fmtNumber(params.qMin, 0)} oder D < ${fmtNumber(params.dMin, 0)} → 0,75`],
            ['Floor / Cap / Combined', `${fmtNumber(params.floor)} / ${fmtNumber(params.cap)} / ${fmtNumber(params.combinedCap)}`],
          ]} />
        ) : <p style={hintS}>Die Antwort enthält keine aktiven V2-Parameter; es wird kein Default als aktiv ausgegeben.</p>}
      </SourceCard>
    </>
  );
}

function FaqChapter() {
  return (
    <>
      <section style={{ ...card, borderColor: '#bbf7d0', background: '#f0fdf4' }}>
        <h3 style={h3}><ShieldCheck size={15} /> Was NIE von allein passiert</h3>
        <ul style={{ ...txt, margin: 0, paddingLeft: 18 }}>
          <li>Kein Regler ändert sich automatisch — Vorschläge ja, angewendet wird nur nach deinem Klick mit Pflichtgrund (protokolliert, rückrollbar).</li>
          <li>Der Markt wird nie Preisquelle; Datenmangel macht nie teurer; der Preis ist serverautoritativ und quotegebunden, unbekannte Abholgebiete fail-closed.</li>
          <li><b>Nicht verstellbar (Vertrag):</b> Provision · Push-Boden · 4,5-km-Radius.</li>
        </ul>
      </section>

      <section style={card}>
        <h3 style={h3}><BookOpenText size={15} /> Schnellrezepte</h3>
        <KeyValues rows={[
          ['Spitze/Flaute stärker atmen lassen', 'Wochenprofil: Durchgriff g rauf und/oder Band weiter — der gemeinsame Deckel fängt Übertreibung'],
          ['Wochenprofil pausieren', 'Panel „Wochenprofil (T)" → Schatten oder Aus → Speichern'],
          ['Weniger Pauschal-Rabatt im Leerlauf', 'Start-ρ Richtung 1,0 anheben'],
          ['Surge früher/kräftiger', 'Surge-Totband senken und/oder Surge-Tempo (kUp) anheben'],
          ['Eine Klasse teurer', 'ihren Klassen-Faktor anheben — wirkt nur dort'],
          ['B1 scharf schalten', 'vorher Testfahrten ausbuchen und im Schatten den Anteil „E roh über 1“ im Monitor prüfen — dann Modus „An“'],
          ['Notfall', 'Engine: Werkseinstellung (mit Rückgängig) · V2: Not-Aus'],
        ]} />
      </section>

      <section style={card}>
        <h3 style={h3}>Häufige Fragen</h3>
        {[
          ['Warum kostet dieselbe Fahrt gerade zu jeder Uhrzeit gleich viel?', 'Prüfe, ob ein Startprofil gesetzt ist und Wochenprofil T auf „An" steht. Ohne gültigen Snapshot bleibt T fail-safe neutral; im Schatten wird nur gerechnet und geloggt.'],
          ['Warum schwankt die Ersparnis-Anzeige trotzdem stark?', 'Die Markt-Referenz atmet mit dem Wochenraster des Marktes — dein Preis (noch) nicht. Die Schwankung kommt komplett vom Vergleichswert.'],
          ['Kann ich ein Uploadprofil als Nachfrage-Startwert setzen?', 'Ja — im Reiter „Normal-Nachfrage" übernimmt der geschützte Owner-Knopf ausschließlich relative Nachfrageform und Belegmengen. Hochgeladene Marktpreise oder Marktreferenzen erreichen den Preisweg niemals.'],
          ['Was passiert beim 🔒 Go-Live-Klick?', 'Er sperrt das zuvor gesetzte aktuelle Startprofil und öffnet ausschließlich zukünftige eigene Ultimate-Fahrten fürs Lernen. Spätere Uploads verändern T nicht; Preise und bestehende Daten werden durch den Klick nicht zurückgesetzt.'],
          ['Woran erkenne ich, ob eine Zahl Messung oder Politik ist?', 'Messbares kommt mit Beleg und Konfidenz; Politik (g, peak, Deckel …) bewusst nur als Richtung mit Begründung. „Kein Zahlenvorschlag, weil …" ist Absicht, kein Fehler.'],
          ['Warum schlägt B1 bei doppelt so viel Nachfrage nicht auf?', 'Absicht: B1 erkennt nur deutliche Ereignisse in Zonen mit Grundlast. Der Rauschschutz (Statistik-Schwelle z) verhindert Aufschläge aus Zufall; eine Verdopplung bleibt deshalb meist ohne Aufschlag, eine Verdreifachung bei normal 4 Fahrten/h wird in etwa 1 von 5 Fenstern erkannt.'],
        ].map(([frage, antwort]) => (
          <div key={frage} style={{ borderBottom: '1px solid #f3f4f6', padding: '8px 0' }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#1f2937', marginBottom: 2 }}>{frage}</div>
            <div style={txt}>{antwort}</div>
          </div>
        ))}
      </section>
    </>
  );
}

export default function EngineGuide() {
  const [activeChapter, setActiveChapter] = useState('ueberblick');
  const [sources, setSources] = useState({ v1: sourceInitial, v2State: sourceInitial, v2Config: sourceInitial });
  const tabRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const requests = [
      ['/admin/engine/config', 'v1'],
      ['/admin/ultimate-v2/state', 'v2State'],
      ['/admin/ultimate-v2/config', 'v2Config'],
    ];
    Promise.allSettled(requests.map(([url]) => api.get(url))).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach((result, index) => {
        const key = requests[index][1];
        next[key] = result.status === 'fulfilled' && result.value?.data
          ? { status: 'ready', data: result.value.data }
          : { status: 'error', data: null };
      });
      setSources(next);
    });
    return () => { cancelled = true; };
  }, []);

  const onTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % CHAPTERS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + CHAPTERS.length) % CHAPTERS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = CHAPTERS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    setActiveChapter(CHAPTERS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="engine-guide">
      <div className="engine-guide-intro" style={{ ...card, background: '#faf5ff', borderColor: '#e9d5ff' }}>
        <h3 style={{ ...h3, margin: 0 }}>Bedienungsanleitung der Preis-Engine — jede Zahl erklärt, mit deinen Live-Werten</h3>
        <span style={hintS}>Read-only: Diese Anleitung lädt deine echten Werte fehlertolerant, speichert nichts und schaltet keinen Modus um. Der Regler-Katalog deckt jeden einstellbaren Wert ab — das erzwingt ein Vertragstest gegen die echte Engine-Konfiguration.</span>
      </div>

      <div role="tablist" aria-label="Kapitel der Engine-Anleitung" className="engine-guide-tabs" style={{ display: 'flex', flexWrap: 'nowrap', gap: 7, overflowX: 'auto', overflowY: 'hidden', padding: '2px 0 10px', marginBottom: 8 }}>
        {CHAPTERS.map((chapter, index) => {
          const selected = activeChapter === chapter.id;
          return (
            <button key={chapter.id} ref={(node) => { tabRefs.current[index] = node; }} type="button" role="tab"
              id={`guide-tab-${chapter.id}`} aria-controls={`guide-panel-${chapter.id}`} aria-selected={selected} tabIndex={selected ? 0 : -1}
              onClick={() => setActiveChapter(chapter.id)} onKeyDown={(event) => onTabKeyDown(event, index)}
              className={selected ? 'is-active' : ''}
              style={{ flexShrink: 0, whiteSpace: 'nowrap', padding: '7px 12px', borderRadius: 999, border: `1px solid ${selected ? '#9c27b0' : '#d1d5db'}`, background: selected ? '#f3e5f5' : '#fff', color: selected ? '#6a1b9a' : '#374151', fontSize: 13, fontWeight: selected ? 800 : 650, cursor: 'pointer' }}>
              {chapter.label}
            </button>
          );
        })}
      </div>

      <div id={`guide-panel-${activeChapter}`} role="tabpanel" aria-labelledby={`guide-tab-${activeChapter}`} tabIndex={0} className="engine-guide-panel">
        {activeChapter === 'ueberblick' && <UeberblickChapter sources={sources} />}
        {activeChapter === 'bedienung' && <BedienungChapter />}
        {activeChapter === 'katalog' && <KatalogChapter sources={sources} />}
        {activeChapter === 'lernen' && <LernenChapter />}
        {activeChapter === 'v2' && <V2Chapter sources={sources} />}
        {activeChapter === 'faq' && <FaqChapter />}
      </div>
    </div>
  );
}
